import React, { useEffect, useLayoutEffect, useMemo } from 'react';

export type RecipeFieldKey =
  | 'date'
  | 'name'
  | 'ageYears'
  | 'ageMonths'
  | 'ageDays'
  | 'weight'
  | 'height'
  | 'temperature'
  | 'prescription';

export interface FieldPosition {
  top: number;
  left: number;
  width?: number;
}

export interface RecipePrintCanvasProps {
  date: string;
  patientName: string;
  ageYears: string;
  ageMonths: string;
  ageDays: string;
  weight: string;
  height: string;
  temperature: string;
  prescription: string;
  className?: string;
  editMode?: boolean;
  selectedField?: RecipeFieldKey | null;
  onSelectField?: (field: RecipeFieldKey) => void;
  fieldPositions?: Partial<Record<RecipeFieldKey, FieldPosition>>;
  onFieldBoundsChange?: (field: RecipeFieldKey, size: { width: number; height: number }) => void;
}

export const DEFAULT_FIELD_POSITIONS: Record<RecipeFieldKey, FieldPosition> = {
  date: { top: 320, left: 160 },
  name: { top: 320, left: 640 },
  ageYears: { top: 395, left: 155, width: 160 },
  ageMonths: { top: 395, left: 295, width: 160 },
  ageDays: { top: 395, left: 435, width: 160 },
  weight: { top: 410, left: 640 },
  height: { top: 410, left: 900 },
  temperature: { top: 410, left: 1170 },
  prescription: { top: 600, left: 200 },
};

export const RECIPE_FIELD_KEYS = Object.keys(DEFAULT_FIELD_POSITIONS) as RecipeFieldKey[];

const RecipePrintCanvas: React.FC<RecipePrintCanvasProps> = ({
  date,
  patientName,
  ageYears,
  ageMonths,
  ageDays,
  weight,
  height,
  temperature,
  prescription,
  className = '',
  editMode = false,
  selectedField,
  onSelectField,
  fieldPositions,
  onFieldBoundsChange,
}) => {
  const mergedPositions = useMemo(() => {
    return RECIPE_FIELD_KEYS.reduce<Record<RecipeFieldKey, FieldPosition>>((acc, key) => {
      const base = DEFAULT_FIELD_POSITIONS[key];
      const override = fieldPositions?.[key];
      acc[key] = override ? { ...base, ...override } : { ...base };
      return acc;
    }, {} as Record<RecipeFieldKey, FieldPosition>);
  }, [fieldPositions]);

  const handleSelect = (field: RecipeFieldKey) => {
    if (editMode) {
      onSelectField?.(field);
    }
  };

  const refs = useMemo(() => {
    return RECIPE_FIELD_KEYS.reduce<Record<RecipeFieldKey, React.RefObject<HTMLDivElement>>>((acc, key) => {
      acc[key] = React.createRef<HTMLDivElement>();
      return acc;
    }, {} as Record<RecipeFieldKey, React.RefObject<HTMLDivElement>>);
  }, []);

  useLayoutEffect(() => {
    if (!onFieldBoundsChange) {
      return;
    }

    (Object.keys(refs) as RecipeFieldKey[]).forEach((key) => {
      const el = refs[key].current;
      if (el) {
        const rect = el.getBoundingClientRect();
        onFieldBoundsChange(key, { width: rect.width, height: rect.height });
      }
    });
  }, [date, patientName, ageYears, ageMonths, ageDays, weight, height, temperature, prescription, editMode, refs, onFieldBoundsChange]);

  useEffect(() => {
    if (!onFieldBoundsChange) {
      return;
    }

    const observers: ResizeObserver[] = [];

    (Object.keys(refs) as RecipeFieldKey[]).forEach((key) => {
      const el = refs[key].current;
      if (!el) {
        return;
      }

      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          onFieldBoundsChange(key, { width, height });
        }
      });

      observer.observe(el);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [refs, onFieldBoundsChange]);

  const classSuffix: Record<RecipeFieldKey, string> = {
    date: 'date',
    name: 'name',
    ageYears: 'age-years',
    ageMonths: 'age-months',
    ageDays: 'age-days',
    weight: 'weight',
    height: 'height',
    temperature: 'temp',
    prescription: 'prescription',
  };

  const styleFor = (field: RecipeFieldKey): React.CSSProperties => {
    const pos = mergedPositions[field];
    const base: React.CSSProperties = {
      top: `${pos.top / 100}cm`,
      left: `${pos.left / 100}cm`,
    };
    if (pos.width != null) {
      base.width = `${pos.width / 100}cm`;
    }
    if (editMode) {
      base.cursor = 'move';
    }
    return base;
  };

  const focusableProps = (field: RecipeFieldKey) => {
    const baseClass = `recipe-field recipe-field-${classSuffix[field]}`;

    if (editMode) {
      return {
        tabIndex: 0,
        onClick: () => handleSelect(field),
        onFocus: () => handleSelect(field),
        className: `${baseClass} ${selectedField === field ? 'recipe-field-selected' : ''}`.trim(),
      };
    }

    return {
      className: baseClass,
    };
  };

  return (
    <div
      className={`recipe-print-page ${editMode ? 'recipe-print-page--edit' : ''} ${className}`.trim()}
    >
      <div ref={refs.date} style={styleFor('date')} {...focusableProps('date')}>
        {date}
      </div>
      <div ref={refs.name} style={styleFor('name')} {...focusableProps('name')}>
        {patientName}
      </div>
      <div ref={refs.ageYears} style={styleFor('ageYears')} {...focusableProps('ageYears')}>
        <span className="recipe-field-value">{ageYears}</span>
        <span className="recipe-field-label">años</span>
      </div>
      <div ref={refs.ageMonths} style={styleFor('ageMonths')} {...focusableProps('ageMonths')}>
        <span className="recipe-field-value">{ageMonths}</span>
        <span className="recipe-field-label">meses</span>
      </div>
      <div ref={refs.ageDays} style={styleFor('ageDays')} {...focusableProps('ageDays')}>
        <span className="recipe-field-value">{ageDays}</span>
        <span className="recipe-field-label">días</span>
      </div>
      <div ref={refs.weight} style={styleFor('weight')} {...focusableProps('weight')}>
        {weight}
      </div>
      <div ref={refs.height} style={styleFor('height')} {...focusableProps('height')}>
        {height}
      </div>
      <div ref={refs.temperature} style={styleFor('temperature')} {...focusableProps('temperature')}>
        {temperature}
      </div>
      <div ref={refs.prescription} style={styleFor('prescription')} {...focusableProps('prescription')}>
        <span className="whitespace-pre-wrap leading-relaxed" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', tabSize: 38 }}>{prescription}</span>
      </div>
    </div>
  );
};

export default RecipePrintCanvas;
